/**
 * Selection System - handles unit selection and command input
 */

import * as THREE from 'three';
import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { MovementComponent } from '@/game/components/Movement';
import { setMovementTarget } from '@/game/systems/MovementSystem';
import type { CombatComponent } from '@/game/components/Combat';
import type { HealthComponent } from '@/game/components/Health';
import type { UnitComponent } from '@/game/components/Unit';
import type { BuildingComponent } from '@/game/components/Building';
import { EventBus } from '@/core/events/EventBus';
import type { Entity, FactionId } from '@/types';
import { useGameStore } from '@/state/gameStore';

export interface SelectionState {
  selectedUnits: Set<Entity>;
  isBoxSelecting: boolean;
  boxStart: { x: number; y: number } | null;
  boxEnd: { x: number; y: number } | null;
  controlGroups: Map<number, Set<Entity>>;
}

export class SelectionSystem implements System {
  private world: World | null = null;
  private state: SelectionState = {
    selectedUnits: new Set(),
    isBoxSelecting: false,
    boxStart: null,
    boxEnd: null,
    controlGroups: new Map(),
  };

  private selectionBox: HTMLElement | null = null;
  private selectionIndicators: Map<Entity, THREE.Mesh> = new Map();
  private scene: THREE.Scene | null = null;

  constructor() {
    this.createSelectionBox();
  }

  setCamera(_camera: THREE.Camera): void {
    // Reserved for future raycasting implementation
  }

  setScene(scene: THREE.Scene): void {
    this.scene = scene;
  }

  private createSelectionBox(): void {
    this.selectionBox = document.createElement('div');
    this.selectionBox.id = 'selection-box';
    this.selectionBox.style.cssText = `
      position: fixed;
      border: 2px solid #00ff00;
      background: rgba(0, 255, 0, 0.1);
      pointer-events: none;
      display: none;
      z-index: 100;
    `;
    document.body.appendChild(this.selectionBox);
  }

  update(world: World, _deltaTime: number): void {
    this.world = world;
    this.updateSelectionIndicators();
  }

  private updateSelectionIndicators(): void {
    if (!this.world || !this.scene) return;

    // Remove indicators for deselected or dead units
    for (const [entity, indicator] of this.selectionIndicators) {
      if (!this.state.selectedUnits.has(entity)) {
        this.scene.remove(indicator);
        indicator.geometry.dispose();
        (indicator.material as THREE.Material).dispose();
        this.selectionIndicators.delete(entity);
        continue;
      }

      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health);
      if (!health || health.current <= 0) {
        this.state.selectedUnits.delete(entity);
        this.scene.remove(indicator);
        indicator.geometry.dispose();
        (indicator.material as THREE.Material).dispose();
        this.selectionIndicators.delete(entity);
        continue;
      }

      // Update indicator position
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position);
      if (pos) {
        indicator.position.set(pos.x, pos.y + 0.1, pos.z);
      }
    }

    // Create indicators for newly selected units
    for (const entity of this.state.selectedUnits) {
      if (this.selectionIndicators.has(entity)) continue;

      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position);
      if (!pos) continue;

      const geometry = new THREE.RingGeometry(0.6, 0.8, 32);
      geometry.rotateX(-Math.PI / 2);
      const material = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
      });
      const indicator = new THREE.Mesh(geometry, material);
      indicator.position.set(pos.x, pos.y + 0.1, pos.z);

      this.scene.add(indicator);
      this.selectionIndicators.set(entity, indicator);
    }
  }

  handleClick(screenX: number, screenY: number, button: number, worldPos: { x: number; z: number }): void {
    if (!this.world) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    if (button === 0) {
      // Left click - select unit or clear selection
      this.handleLeftClick(screenX, screenY, worldPos, playerFaction);
    } else if (button === 2) {
      // Right click - issue command
      this.handleRightClick(worldPos, playerFaction);
    }
  }

  private handleLeftClick(_screenX: number, _screenY: number, worldPos: { x: number; z: number }, playerFaction: FactionId): void {
    if (!this.world) return;

    // Check if clicking on a unit
    const clickedUnit = this.getUnitAtPosition(worldPos.x, worldPos.z, playerFaction);

    if (clickedUnit !== null) {
      // Select the clicked unit
      this.clearSelection();
      this.state.selectedUnits.add(clickedUnit);
      this.emitSelectionChange();
    } else {
      // Clear selection if clicking on empty space
      this.clearSelection();
    }
  }

  private handleRightClick(worldPos: { x: number; z: number }, playerFaction: FactionId): void {
    if (!this.world || this.state.selectedUnits.size === 0) return;

    // Check if clicking on an enemy unit
    const enemyUnit = this.getEnemyUnitAtPosition(worldPos.x, worldPos.z, playerFaction);

    if (enemyUnit !== null) {
      // Issue attack command
      this.issueAttackCommand(enemyUnit);
      return;
    }

    // Check if clicking on an enemy building
    const enemyBuilding = this.getEnemyBuildingAtPosition(worldPos.x, worldPos.z, playerFaction);

    if (enemyBuilding !== null) {
      // Issue attack command against building
      this.issueAttackCommand(enemyBuilding);
      return;
    }

    // Issue move command
    this.issueMoveCommand(worldPos.x, worldPos.z);
  }

  private getUnitAtPosition(x: number, z: number, factionId: FactionId): Entity | null {
    if (!this.world) return null;

    const units = this.world.query([
      ComponentTypes.Position,
      ComponentTypes.Faction,
      ComponentTypes.Unit,
      ComponentTypes.Health,
    ]);

    let closestUnit: Entity | null = null;
    let closestDist = 2.0; // Selection radius

    for (const entity of units) {
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;

      if (faction.factionId !== factionId || health.current <= 0) continue;

      const dx = pos.x - x;
      const dz = pos.z - z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < closestDist) {
        closestDist = dist;
        closestUnit = entity;
      }
    }

    return closestUnit;
  }

  private getEnemyUnitAtPosition(x: number, z: number, playerFaction: FactionId): Entity | null {
    if (!this.world) return null;

    const units = this.world.query([
      ComponentTypes.Position,
      ComponentTypes.Faction,
      ComponentTypes.Health,
      ComponentTypes.Unit,
    ]);

    let closestUnit: Entity | null = null;
    let closestDist = 2.0;

    for (const entity of units) {
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;

      if (faction.factionId === playerFaction || health.current <= 0) continue;

      const dx = pos.x - x;
      const dz = pos.z - z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < closestDist) {
        closestDist = dist;
        closestUnit = entity;
      }
    }

    return closestUnit;
  }

  private getEnemyBuildingAtPosition(x: number, z: number, playerFaction: FactionId): Entity | null {
    if (!this.world) return null;

    const buildings = this.world.query([
      ComponentTypes.Position,
      ComponentTypes.Faction,
      ComponentTypes.Health,
      ComponentTypes.Building,
    ]);

    let closestBuilding: Entity | null = null;
    let closestDist = 3.0; // Larger radius for buildings

    for (const entity of buildings) {
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;

      if (faction.factionId === playerFaction || health.current <= 0) continue;

      const dx = pos.x - x;
      const dz = pos.z - z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < closestDist) {
        closestDist = dist;
        closestBuilding = entity;
      }
    }

    return closestBuilding;
  }

  private issueMoveCommand(x: number, z: number): void {
    if (!this.world) return;

    const unitCount = this.state.selectedUnits.size;
    let index = 0;

    for (const entity of this.state.selectedUnits) {
      const movement = this.world.getComponent<MovementComponent>(entity, ComponentTypes.Movement);
      const combat = this.world.getComponent<CombatComponent>(entity, ComponentTypes.Combat);

      if (movement) {
        // Formation spread for multiple units
        const angle = (index / unitCount) * Math.PI * 2;
        const spread = unitCount > 1 ? Math.min(unitCount * 0.5, 5) : 0;
        const offsetX = Math.cos(angle) * spread;
        const offsetZ = Math.sin(angle) * spread;

        setMovementTarget(movement, x + offsetX, z + offsetZ, true);
      }

      // Clear attack target when moving
      if (combat) {
        combat.target = null;
      }

      index++;
    }

    EventBus.emit('command:move', { x, z, unitCount });
  }

  private issueAttackCommand(target: Entity): void {
    if (!this.world) return;

    const targetPos = this.world.getComponent<PositionComponent>(target, ComponentTypes.Position);

    for (const entity of this.state.selectedUnits) {
      const combat = this.world.getComponent<CombatComponent>(entity, ComponentTypes.Combat);
      const movement = this.world.getComponent<MovementComponent>(entity, ComponentTypes.Movement);

      if (combat) {
        combat.target = target;
      }

      // Move toward target
      if (movement && targetPos) {
        setMovementTarget(movement, targetPos.x, targetPos.z, true);
      }
    }

    EventBus.emit('command:attack', {
      targetId: target,
      unitCount: this.state.selectedUnits.size,
      x: targetPos?.x,
      z: targetPos?.z,
    });
  }

  startBoxSelection(screenX: number, screenY: number): void {
    this.state.isBoxSelecting = true;
    this.state.boxStart = { x: screenX, y: screenY };
    this.state.boxEnd = { x: screenX, y: screenY };
    this.updateSelectionBoxVisual();
  }

  updateBoxSelection(screenX: number, screenY: number): void {
    if (!this.state.isBoxSelecting) return;
    this.state.boxEnd = { x: screenX, y: screenY };
    this.updateSelectionBoxVisual();
  }

  endBoxSelection(getWorldPos: (x: number, y: number) => { x: number; z: number }): void {
    if (!this.state.isBoxSelecting || !this.state.boxStart || !this.state.boxEnd) return;
    if (!this.world) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    // Get world coordinates of box corners
    const start = getWorldPos(this.state.boxStart.x, this.state.boxStart.y);
    const end = getWorldPos(this.state.boxEnd.x, this.state.boxEnd.y);

    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minZ = Math.min(start.z, end.z);
    const maxZ = Math.max(start.z, end.z);

    // Select all player units within the box
    this.clearSelection();

    const units = this.world.query([
      ComponentTypes.Position,
      ComponentTypes.Faction,
      ComponentTypes.Unit,
      ComponentTypes.Health,
    ]);

    for (const entity of units) {
      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;

      if (faction.factionId !== playerFaction || health.current <= 0) continue;

      if (pos.x >= minX && pos.x <= maxX && pos.z >= minZ && pos.z <= maxZ) {
        this.state.selectedUnits.add(entity);
      }
    }

    this.emitSelectionChange();
    this.hideSelectionBox();
  }

  private updateSelectionBoxVisual(): void {
    if (!this.selectionBox || !this.state.boxStart || !this.state.boxEnd) return;

    const left = Math.min(this.state.boxStart.x, this.state.boxEnd.x);
    const top = Math.min(this.state.boxStart.y, this.state.boxEnd.y);
    const width = Math.abs(this.state.boxEnd.x - this.state.boxStart.x);
    const height = Math.abs(this.state.boxEnd.y - this.state.boxStart.y);

    this.selectionBox.style.display = 'block';
    this.selectionBox.style.left = `${left}px`;
    this.selectionBox.style.top = `${top}px`;
    this.selectionBox.style.width = `${width}px`;
    this.selectionBox.style.height = `${height}px`;
  }

  private hideSelectionBox(): void {
    if (this.selectionBox) {
      this.selectionBox.style.display = 'none';
    }
    this.state.isBoxSelecting = false;
    this.state.boxStart = null;
    this.state.boxEnd = null;
  }

  setControlGroup(groupNumber: number): void {
    if (this.state.selectedUnits.size === 0) return;
    this.state.controlGroups.set(groupNumber, new Set(this.state.selectedUnits));
  }

  selectControlGroup(groupNumber: number): void {
    const group = this.state.controlGroups.get(groupNumber);
    if (!group) return;

    this.clearSelection();
    for (const entity of group) {
      // Verify entity still exists and is alive
      if (this.world) {
        const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health);
        if (health && health.current > 0) {
          this.state.selectedUnits.add(entity);
        }
      }
    }
    this.emitSelectionChange();
  }

  private clearSelection(): void {
    this.state.selectedUnits.clear();
    this.emitSelectionChange();
  }

  private emitSelectionChange(): void {
    useGameStore.getState().setSelectedEntities(Array.from(this.state.selectedUnits));
    EventBus.emit('selection:changed', {
      selectedCount: this.state.selectedUnits.size,
      entities: Array.from(this.state.selectedUnits),
    });
  }

  getSelectedUnits(): Set<Entity> {
    return this.state.selectedUnits;
  }

  getSelectedUnitInfo(): Array<{ entity: Entity; type: string; health: number; maxHealth: number }> {
    if (!this.world) return [];

    const info: Array<{ entity: Entity; type: string; health: number; maxHealth: number }> = [];

    for (const entity of this.state.selectedUnits) {
      const unit = this.world.getComponent<UnitComponent>(entity, ComponentTypes.Unit);
      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health);

      if (unit && health) {
        info.push({
          entity,
          type: unit.unitType,
          health: health.current,
          maxHealth: health.max,
        });
      }
    }

    return info;
  }

  dispose(): void {
    if (this.selectionBox) {
      this.selectionBox.remove();
    }

    for (const [_, indicator] of this.selectionIndicators) {
      if (this.scene) {
        this.scene.remove(indicator);
      }
      indicator.geometry.dispose();
      (indicator.material as THREE.Material).dispose();
    }
    this.selectionIndicators.clear();
  }
}
